import React, { useState } from 'react';
import { updateUserQNAStatus } from '../../../../../../../../../../utils/api_service/assessments_api';
import { AssessmentQuestionResult, AttemptSkill } from '../../../../../../../../../../utils/types/assessment_types';
import { IDetailedResults, ISection, ISummaryStats } from '../Interfaces/Interfaces';

export const useDetailedResults = (props: IDetailedResults) => {
  const {
    assessmentData: initialAssessmentData,
    beiResults,
    loading,
    error,
  } = props;
  const [assessmentData, setAssessmentData] = useState(initialAssessmentData);
  const [openSections, setOpenSections] = useState<Set<string>>(new Set());
  const [openQuestions, setOpenQuestions] = useState<Set<string>>(new Set());
  const [detailedQuestions, setDetailedQuestions] = useState<
    AssessmentQuestionResult[]
  >([]);

  // Calculate stats before any conditional returns
  const getComprehensiveStats = React.useCallback(() => {
    const stats = {
      overallScore: 0,
      technicalScore: 0,
      behavioralScore: 0,
      totalQuestions: 0,
      correctAnswers: 0,
      timeSpent: 0,
      attributes: 0,
    };

    // BEI Statistics
    if (beiResults && beiResults.length > 0) {
      stats.behavioralScore = Math.round(
        beiResults.reduce((sum, result) => sum + result.score, 0) /
          beiResults.length,
      );
      stats.attributes += beiResults.length;
      stats.totalQuestions += beiResults.reduce(
        (sum, result) => sum + result.questionAnswer.length,
        0,
      );
    }

    // Assessment Statistics
    if (assessmentData?.skills && assessmentData?.questions) {
      const totalCorrect = assessmentData.skills.reduce(
        (sum: number, skill: any) => sum + skill.correctCount,
        0,
      );
      const totalQuestions = assessmentData.skills.reduce(
        (sum: number, skill: any) => sum + skill.totalCount,
        0,
      );

      if (totalQuestions > 0) {
        stats.technicalScore = Math.round(
          (totalCorrect / totalQuestions) * 100,
        );
      }

      stats.correctAnswers += totalCorrect;
      stats.totalQuestions += totalQuestions;
      stats.attributes += assessmentData.skills.length;
      stats.timeSpent = assessmentData.totalTime || 0;
    }

    // Calculate overall score
    if (stats.technicalScore > 0 && stats.behavioralScore > 0) {
      stats.overallScore = Math.round(
        (stats.technicalScore + stats.behavioralScore) / 2,
      );
    } else if (stats.technicalScore > 0) {
      stats.overallScore = stats.technicalScore;
    } else if (stats.behavioralScore > 0) {
      stats.overallScore = stats.behavioralScore;
    }

    return stats;
  }, [beiResults, assessmentData]);

  const comprehensiveStats = React.useMemo(
    () => getComprehensiveStats(),
    [getComprehensiveStats],
  );

  const summaryStats = React.useMemo<ISummaryStats>(() => {
    if (!beiResults || beiResults.length === 0) {
      return {
        beiAttributes: [],
        overallCompetencyScore: 0,
        situationalResponses: 0,
        starFrameworkAdherence: 0,
        totalBehavioralExamples: 0,
        totalQuestions: 0,
        totalFollowUps: 0,
        averageScore: 0,
        attributesCount: 0,
      };
    }

    const beiAttributes = beiResults.map((result) => ({
      name: result.attribute,
      score: result.score,
      competencyLevel: (result.score >= 85
        ? 'Expert'
        : result.score >= 70
          ? 'Proficient'
          : result.score >= 55
            ? 'Developing'
            : 'Basic') as 'Expert' | 'Proficient' | 'Developing' | 'Basic',
      evidenceCount: result.questionAnswer.length,
    }));

    const overallCompetencyScore = Math.round(
      beiResults.reduce((sum, result) => sum + result.score, 0) /
        beiResults.length,
    );

    const starFrameworkAdherence =
      beiResults.reduce((sum, result) => {
        const hasStarComponents = result.questionAnswer.filter(
          (qa: { answer: string }) =>
            qa.answer?.toLowerCase().includes('situation') &&
            qa.answer?.toLowerCase().includes('task') &&
            qa.answer?.toLowerCase().includes('action') &&
            qa.answer?.toLowerCase().includes('result'),
        ).length;
        return sum + (hasStarComponents / result.questionAnswer.length) * 100;
      }, 0) / beiResults.length;

    const totalBehavioralExamples = beiResults.reduce(
      (sum, result) => sum + result.questionAnswer.length,
      0,
    );

    const situationalResponses = beiResults.reduce(
      (sum, result) =>
        sum +
        result.questionAnswer.filter(
          (qa: { answer: string }) =>
            qa.answer?.toLowerCase().includes('situation') ||
            qa.answer?.toLowerCase().includes('scenario') ||
            qa.answer?.toLowerCase().includes('example'),
        ).length,
      0,
    );

    return {
      beiAttributes,
      overallCompetencyScore,
      situationalResponses,
      starFrameworkAdherence,
      totalBehavioralExamples,
      totalQuestions: totalBehavioralExamples,
      totalFollowUps: beiResults.reduce(
        (sum, result) => sum + result.followupCount,
        0,
      ),
      averageScore: overallCompetencyScore,
      attributesCount: beiAttributes.length,
    };
  }, [beiResults]);

  // Helper function to format time from seconds to mm:ss
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs
      .toString()
      .padStart(2, '0')}`;
  };

  // Convert BEI results to sections format
  const getBehavioralSections = (): ISection[] => {
    if (!beiResults || beiResults.length === 0) return [];

    return beiResults.map((result) => {
      return {
        title: `${result.attribute} Assessment`,
        totalQuestions: result.questionAnswer.length,
        followUpQuestions: result.followupCount,
        score: result.score || 0,
        // Add section-level STAR breakdown for the card display
        starBreakdown: result.starScore
          ? {
            situation: { score: result.starScore.situation, total: 100 },
            actions: { score: result.starScore.action, total: 100 },
            task: { score: result.starScore.task, total: 100 },
            results: { score: result.starScore.result, total: 100 },
          }
          : undefined,
        questions: result.questionAnswer.map(
          (qa: {
            question: any;
            answer: any;
            videoFilename: any;
            followupReason: any;
          }) => ({
            question: qa.question,
            score: result.score || 0,
            timeTaken: '00:00', // Time not provided in BEI API
            skillAttribute: result.attribute,
            response: qa.answer,
            videoResponse: qa.videoFilename,
            starBreakdown: result.starScore
              ? {
                situation: { score: result.starScore.situation, total: 100 },
                actions: { score: result.starScore.action, total: 100 },
                task: { score: result.starScore.task, total: 100 },
                results: { score: result.starScore.result, total: 100 },
              }
              : undefined,
            followUpQuestions: qa.followupReason ? [qa.followupReason] : [],
          }),
        ),
      };
    });
  };

  // Convert assessment data to sections format (grouped by skills)
  const getAssessmentSections = (): ISection[] => {
    if (!assessmentData || !assessmentData.skills || !assessmentData.questions)
      return [];

    return assessmentData.skills.map((skill) => {
      const skillQuestions = assessmentData.questions.filter(
        (q) => q.skillId === skill.skillId,
      );

      return {
        title: `${skill.skillName} Assessment`,
        totalQuestions: skill.totalCount,
        followUpQuestions: 0, // Assessment questions don't have follow-ups
        score:
          skill.totalCount > 0
            ? Math.round((skill.correctCount / skill.totalCount) * 100)
            : 0,
        skillId: skill.skillId,
        questions: skillQuestions.map((q) => ({
          question: q.systemQuestion,
          score: q.maxScore > 0 ? Math.round((q.score / q.maxScore) * 100) : 0,
          timeTaken: formatTime(q.timeTaken),
          skillAttribute: skill.skillName,
          response: q.userAnswer,
          videoResponse: false, // Assessment questions typically don't have video
          starBreakdown: undefined, // No STAR breakdown for assessment questions
          followUpQuestions: [], // No follow-up questions
          skillId: skill.skillId,
        })),
      };
    });
  };

  // Memoize sections to prevent unnecessary recalculations
  const behavioralSections = React.useMemo(
    () => getBehavioralSections(),
    [beiResults],
  );
  const assessmentSections = React.useMemo(
    () => getAssessmentSections(),
    [assessmentData],
  );

  // Calculate summary statistics

  // Handle multiple question toggles
  const handleToggleQuestion = (questionId: string) => {
    setOpenQuestions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  // Handle question status change
  const handleQuestionStatusChange = async (
    questionId: string,
    isApproved: boolean,
  ) => {
    try {
      await updateUserQNAStatus(questionId, isApproved);
      // Update local state to reflect the change
      if (assessmentData?.questions) {
        const updatedQuestions = assessmentData.questions.map((q) => {
          if (q.id === questionId) {
            return {
              ...q,
              status: isApproved ? 'approved' : 'incorrect',
              score: isApproved ? q.maxScore : 0, // Update score based on approval
            };
          }
          return q;
        });
        // Update assessmentData with the new questions
        setAssessmentData((prev) =>
          prev
            ? {
              ...prev,
              questions: updatedQuestions,
            }
            : prev,
        );
      }
    } catch (error) {
      console.error('Error updating question status:', error);
      // Show error toast or message to user
    }
  };

  // Handle section toggle - Allow multiple sections to be open
  const handleSectionToggle = (sectionTitle: string) => {
    setOpenSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionTitle)) {
        newSet.delete(sectionTitle);
      } else {
        newSet.add(sectionTitle);
      }
      return newSet;
    });
  };

  // Initialize detailedQuestions when assessmentData changes
  React.useEffect(() => {
    if (initialAssessmentData?.questions) {
      setDetailedQuestions(initialAssessmentData.questions);
    }
  }, [initialAssessmentData]);

  // Initialize with all sections closed only on initial mount
  React.useEffect(() => {
    setOpenSections(new Set()); // Start with all sections closed
  }, []); // Empty dependency array means this runs only once on mount

  // Handle partial or empty state with detailed information
  const hasBeiResults = beiResults && beiResults.length > 0;
  const hasAssessmentData =
    assessmentData &&
    assessmentData.questions &&
    assessmentData.questions.length > 0;

  const candidateInfo = React.useMemo(() => {
    const firstName = assessmentData?.user?.firstName || '';
    const lastName = assessmentData?.user?.lastName || '';
    const email = assessmentData?.user?.email || '';
    const fullName =
      firstName && lastName
        ? `${firstName} ${lastName}`
        : firstName || lastName || 'Candidate Name';

    return {
      name: fullName,
      email: email,
      company:
        assessmentData?.assessment?.jobPosition || 'Position Not Specified',
      date: assessmentData?.assessment?.creationDateTime
        ? new Date(
          assessmentData.assessment.creationDateTime * 1000,
        ).toLocaleDateString()
        : assessmentData?.startTime
          ? new Date(assessmentData.startTime * 1000).toLocaleDateString()
          : new Date().toLocaleDateString(),
      avatar: '/api/placeholder/64/64',
    };
  }, [assessmentData]);

  const updateDetailedQuestionArray = (
    questionResult: AssessmentQuestionResult,
    getLinkStatus?: boolean,
  ) => {
    let detailedQuestionsCopy = [...detailedQuestions];
    if (!getLinkStatus) {
      questionResult.approved = true;
    }
    let index = detailedQuestionsCopy.findIndex(
      (item) => item.id === questionResult.id,
    );
    detailedQuestionsCopy[index] = questionResult;
    setDetailedQuestions(detailedQuestionsCopy);
  };
  return {
    assessmentSections,
    candidateInfo,
    summaryStats,
    hasAssessmentData,
    hasBeiResults,
    openSections,
    handleSectionToggle,
    detailedQuestions,
    formatTime,
    updateDetailedQuestionArray,
    behavioralSections,
    handleToggleQuestion,
    openQuestions,
    handleQuestionStatusChange,
  };
};
